import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader, Pill } from "../components/Table";
import { Rocket, ShieldCheck, Terminal, Copy } from "@phosphor-icons/react";

const Cmd = ({ children }) => (
  <div className="flex items-start gap-2 bg-background border border-border rounded-sm px-3 py-2 font-mono text-[11px] break-all">
    <Terminal size={13} className="text-primary mt-0.5 shrink-0" />
    <code className="flex-1 whitespace-pre-wrap">{children}</code>
    <button onClick={() => { navigator.clipboard?.writeText(String(children)); toast.success("Copied"); }} className="text-muted-foreground hover:text-foreground shrink-0"><Copy size={13} /></button>
  </div>
);

const Step = ({ n, title, children }) => (
  <div className="flex gap-3">
    <div className="w-7 h-7 rounded-full brand-gradient text-white font-bold text-sm flex items-center justify-center shrink-0">{n}</div>
    <div className="flex-1 space-y-2 pb-4">
      <div className="font-display font-bold text-base">{title}</div>
      {children}
    </div>
  </div>
);

export default function DeploymentGuide() {
  const [conn, setConn] = useState(null);
  useEffect(() => { api.get("/gateway/connection-info").then((r) => setConn(r.data)).catch(() => {}); }, []);

  const brands = conn?.brands || [];
  const ports = brands.map((b) => b.port).filter(Boolean);

  return (
    <div className="p-5 space-y-4 max-w-4xl">
      <PageHeader title="Deployment Guide" subtitle="Expose your brand ports on a public VPS so real trackers connect" />

      <div className="bg-card border border-border rounded-sm p-4 flex items-start gap-3" data-testid="deploy-intro">
        <Rocket size={22} weight="fill" className="text-primary mt-0.5" />
        <div className="text-sm text-muted-foreground">
          This preview environment cannot accept raw device TCP connections. To go live, run this backend on a public
          Linux VPS (with a static public IP) and open each GPS brand's port through the firewall. Devices then connect
          straight to <code className="text-primary">YOUR_PUBLIC_IP:PORT</code>.
        </div>
      </div>

      <div className="bg-card border border-border rounded-sm p-5 space-y-4">
        <Step n={1} title="Get a VPS with a public static IP">
          <p className="text-sm text-muted-foreground">Any provider (AWS EC2, DigitalOcean, Hetzner, Contabo). Ubuntu 22.04+, min 2 vCPU / 4 GB RAM. Note the public IP — that's what devices dial.</p>
        </Step>
        <Step n={2} title="Install runtime & deploy this app">
          <Cmd>sudo apt update && sudo apt install -y python3-pip mongodb nodejs npm</Cmd>
          <p className="text-sm text-muted-foreground">Copy the backend + frontend, set backend <code className="text-primary">.env</code> (MONGO_URL, DB_NAME) and <code className="text-primary">GATEWAY_HOST=YOUR_PUBLIC_IP</code>, then run the backend with a process manager (systemd / pm2 / supervisor).</p>
        </Step>
        <Step n={3} title="Open the firewall for each brand port">
          <p className="text-sm text-muted-foreground">Your registered GPS brands currently use these ports:</p>
          <div className="flex flex-wrap gap-2 my-1">
            {ports.length ? brands.map((b) => <Pill key={b.port} color="#00c8dc">{b.name}: {b.port}</Pill>)
              : <span className="text-xs text-muted-foreground">No brands yet — add them in Device Inventory → GPS Brands.</span>}
          </div>
          {ports.map((p) => <Cmd key={p}>sudo ufw allow {p}/tcp</Cmd>)}
          {!ports.length && <Cmd>sudo ufw allow 10000:65535/tcp</Cmd>}
          <Cmd>sudo ufw allow 22/tcp && sudo ufw enable</Cmd>
          <p className="text-xs text-muted-foreground">Also open the same ports in your cloud provider's Security Group / network firewall.</p>
        </Step>
        <Step n={4} title="Point each device to your server">
          <p className="text-sm text-muted-foreground">In each tracker's SMS/config, set the server IP to <code className="text-primary">{conn?.host || "YOUR_PUBLIC_IP"}</code> and the port to its brand's port above. Confirm packets arrive on the <b>Raw Data Feed</b>, then <b>Activate</b> them.</p>
        </Step>
        <Step n={5} title="Secure it">
          <div className="flex items-start gap-2 text-sm text-muted-foreground"><ShieldCheck size={16} className="text-[#34C759] mt-0.5" /> Use HTTPS (Caddy/Nginx + Let's Encrypt) for the web portal, keep MongoDB bound to localhost, and take daily DB backups. Devices connect to each brand's raw TCP port with no key — protect those ports at the firewall (allow only your carrier/device IP ranges where possible).</div>
        </Step>
      </div>
    </div>
  );
}
